import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  getMySubscriptionApi,
  getMyUsageApi,
  changeSubscriptionTierApi,
  payInvoiceApi,
  getMyBankDetailsApi,
  type EmployerSubscription,
  type EmployerBankDetails,
  type UsageResponse,
} from '@/lib/billing'
import {
  LoaderIcon,
  CreditCardIcon,
  FileTextIcon,
  BuildingIcon,
  UsersIcon,
  CalendarIcon,
  ClockIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknoteIcon,
  ExternalLinkIcon,
  TrendingUpIcon,
  InfoIcon,
  RocketIcon,
  ShieldCheckIcon,
  LockKeyholeIcon,
  ArrowUpCircleIcon,
  GlobeIcon,
  ArrowRightFromLineIcon,
  UploadIcon,
  FileSpreadsheetIcon,
  HeadphonesIcon,
  UserRoundCogIcon,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  trial: { label: 'Trial', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  past_due: { label: 'Past due', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
  suspended: { label: 'Suspended', color: 'bg-destructive/10 text-destructive' },
  complimentary: { label: 'Complimentary', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
}


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/billing/')({
  component: () => {
    const navigate = useNavigate()
    const [subscription, setSubscription] = useState<EmployerSubscription | null>(null)
    const [usage, setUsage] = useState<UsageResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [confirmTargetTier, setConfirmTargetTier] = useState<'growth' | 'enterprise' | null>(null)
    const [upgrading, setUpgrading] = useState(false)
    const [bankDetails, setBankDetails] = useState<EmployerBankDetails | null>(null)
    const [paying, setPaying] = useState(false)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const [subRes, usageRes, bankRes] = await Promise.all([
            getMySubscriptionApi(),
            getMyUsageApi(),
            getMyBankDetailsApi().catch(() => null),
          ])
          if (!cancelled) {
            setSubscription(subRes.subscription)
            setUsage(usageRes)
            setBankDetails(bankRes)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(err instanceof Error ? err.message : 'Failed to load billing info')
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [])

    if (loading) {
      return (
        <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
          <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
          <p className="ml-2 text-sm text-muted-foreground">Loading billing information…</p>
        </div>
      )
    }

    const statusStyle = subscription
      ? STATUS_STYLES[subscription.status] ?? { label: subscription.status, color: 'bg-muted text-muted-foreground' }
      : null

    const handlePayPendingChange = async () => {
      const invoiceId = subscription?.pending_change?.invoice_id
      if (!invoiceId || paying) return
      setPaying(true)
      try {
        const result = await payInvoiceApi(invoiceId)
        window.location.href = result.url
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Unable to start card payment')
        setPaying(false)
      }
    }

    return (
      <>
        {!subscription ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <CreditCardIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No subscription found</p>
            <p className="text-sm text-muted-foreground">Your account hasn't been set up with a billing plan yet.</p>
          </div>
        ) : (
          <>
            {/* ── Subscription Status Card ── */}
            <div className="mb-6 rounded-xl border border-border bg-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <CreditCardIcon className="size-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-foreground">Subscription</h2>
                      {statusStyle && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyle.color}`}>
                          {subscription.status === 'complimentary' && <BanknoteIcon className="size-3" />}
                          {subscription.status === 'active' && <CheckCircleIcon className="size-3" />}
                          {subscription.status === 'past_due' && <AlertTriangleIcon className="size-3" />}
                          {subscription.status === 'suspended' && <XCircleIcon className="size-3" />}
                          {subscription.status === 'trial' && <ClockIcon className="size-3" />}
                          {statusStyle.label}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 flex items-center gap-2 text-sm text-muted-foreground">
                      {subscription.plan_name}
                      {subscription.tier && (
                        <TierBadge tier={subscription.tier} />
                      )}
                    </p>
                    {subscription.billing_cycle && subscription.billing_cycle !== 'monthly' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        <ClockIcon className="size-3" />
                        {subscription.billing_cycle === 'annual' ? 'Billed annually' : subscription.billing_cycle}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <BillingField
                  icon={<UsersIcon className="size-4" />}
                  label="Active users"
                  value={String(subscription.active_users)}
                />
                <BillingField
                  icon={<BanknoteIcon className="size-4" />}
                  label="Price per user"
                  value={formatCentsWithCurrency(subscription.price_per_user_cents, subscription.currency)}
                  badge={subscription.override_price_per_user_cents != null ? 'Custom pricing' : undefined}
                />
                <BillingField
                  icon={<BanknoteIcon className="size-4" />}
                  label="Minimum monthly"
                  value={formatCentsWithCurrency(subscription.minimum_monthly_cents, subscription.currency)}
                />
                <BillingField
                  icon={<TrendingUpIcon className="size-4" />}
                  label="Estimated monthly"
                  value={formatCentsWithCurrency(subscription.estimated_monthly, subscription.currency)}
                />
                {subscription.trial_days_remaining != null && (
                  <BillingField
                    icon={<ClockIcon className="size-4" />}
                    label="Trial days remaining"
                    value={String(subscription.trial_days_remaining)}
                  />
                )}
                <BillingField
                  icon={<CalendarIcon className="size-4" />}
                  label="Current period"
                  value={`${formatDate(subscription.current_period_start)} — ${formatDate(subscription.current_period_end)}`}
                />
              </div>

              {/* ── Annual Billing Savings ── */}
              {subscription.billing_cycle === 'monthly' && subscription.tier && (
                <div className="mt-4 rounded-lg bg-amber-500/[0.06] border border-amber-500/20 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <ClockIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
                      <div>
                        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                          Save {subscription.tier === 'growth' ? '29%' : '20%'} with annual billing
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {subscription.tier === 'growth' ? '€5' : '€12'}/user/month when billed yearly — one upfront payment, lower gateway fees.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-amber-600 hover:shadow-md"
                      onClick={() => setConfirmTargetTier(subscription.tier)}
                    >
                      Switch to annual
                    </button>
                  </div>
                </div>
              )}

              {subscription.complimentary_type && (
                <div className="mt-4 rounded-lg bg-purple-500/10 px-4 py-3">
                  <p className="text-xs font-medium text-purple-600 dark:text-purple-400">
                    Complimentary account · {subscription.complimentary_type.replace(/_/g, ' ')}
                    {subscription.complimentary_expires_at && ` · Expires ${formatDate(subscription.complimentary_expires_at)}`}
                  </p>
                  {subscription.complimentary_notes && (
                    <p className="mt-1 text-xs text-muted-foreground">{subscription.complimentary_notes}</p>
                  )}
                </div>
              )}
            </div>

            {/* ── Pending plan payment ── */}
            {subscription.pending_change && (
              <PendingPlanPayment
                pendingChange={subscription.pending_change}
                bankDetails={bankDetails}
                paying={paying}
                onPayWithCard={handlePayPendingChange}
              />
            )}

            {/* ── Tier Features ── */}
            <TierFeatureComparison
              tier={subscription.tier}
              planPricePerUserCents={subscription.next_tier_plan_price_per_user_cents ?? subscription.plan_price_per_user_cents}
              currency={subscription.currency}
              onRequestUpgrade={setConfirmTargetTier}
            />

            {/* ── Upgrade confirmation dialog ── */}
            <UpgradeConfirmDialog
              open={confirmTargetTier !== null}
              currentTier={subscription.tier}
              targetTier={confirmTargetTier}
              isBillingCycleChange={confirmTargetTier === subscription.tier}
              pricePerUserCents={subscription.price_per_user_cents}
              targetPricePerUserCents={subscription.next_tier_plan_price_per_user_cents ?? subscription.plan_price_per_user_cents}
              activeUsers={subscription.active_users}
              currency={subscription.currency}
              upgrading={upgrading}
              onConfirm={async () => {
                if (!confirmTargetTier) return
                setUpgrading(true)
                try {
                  const isAnnualSwitch = confirmTargetTier === subscription.tier
                  const result = await changeSubscriptionTierApi(
                    confirmTargetTier,
                    isAnnualSwitch ? 'annual' : undefined,
                  )
                  toast.success(result.message)
                  const subRes = await getMySubscriptionApi()
                  setSubscription(subRes.subscription)
                  setConfirmTargetTier(null)
                  navigate({ to: '/dashboard/billing/invoices/$invoiceId', params: { invoiceId: String(result.invoice_id) } })
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to change tier')
                } finally {
                  setUpgrading(false)
                }
              }}
              onCancel={() => {
                if (!upgrading) setConfirmTargetTier(null)
              }}
            />

            {/* ── Usage History ── */}
            {usage && usage.usage_history.length > 0 && (
              <div className="mb-6 rounded-xl border border-border bg-card p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <UsersIcon className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Usage history</h3>
                    <p className="text-xs text-muted-foreground">{usage.current_active_users} current active users</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {usage.usage_history.map((entry) => (
                    <div key={entry.snapshot_date} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                      <span className="text-xs text-muted-foreground">{formatDate(entry.snapshot_date)}</span>
                      <span className="text-xs font-medium tabular-nums">{entry.active_user_count} users</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Quick Links ── */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Link
                to="/dashboard/billing/invoices"
                className="group rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <FileTextIcon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">Invoice history</p>
                    <p className="text-xs text-muted-foreground">View and download past invoices</p>
                  </div>
                  <ExternalLinkIcon className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </Link>

              <Link
                to="/dashboard/billing/bank-details"
                className="group rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <BuildingIcon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">Bank details</p>
                    <p className="text-xs text-muted-foreground">PeerPass payment instructions</p>
                  </div>
                  <ExternalLinkIcon className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </Link>
            </div>
          </>
        )}
      </>
    )
  },
})

// ── Tier feature definitions ────────────────────────────────────────

const TIER_FEATURES = [
  {
    key: 'unlimited_pots',
    label: 'Unlimited funding circles',
    description: 'Create and manage unlimited funding circles',
    icon: GlobeIcon,
    growth: true,
    enterprise: true,
  },
  {
    key: 'bulk_import',
    label: 'CSV bulk user import',
    description: 'Import employees via CSV in bulk',
    icon: UploadIcon,
    growth: true,
    enterprise: true,
  },
  {
    key: 'tax_forms',
    label: 'Tax form generation',
    description: '1099-INT, SAT A1, and future country forms',
    icon: FileSpreadsheetIcon,
    growth: true,
    enterprise: true,
  },
  {
    key: 'sso',
    label: 'SSO (SAML 2.0 / OIDC)',
    description: 'Single sign-on with Okta, Azure AD, Google Workspace',
    icon: ShieldCheckIcon,
    growth: false,
    enterprise: true,
  },
  {
    key: 'api_access',
    label: 'API access',
    description: 'Build custom integrations via our REST API',
    icon: ArrowRightFromLineIcon,
    growth: false,
    enterprise: true,
  },
  {
    key: 'slas',
    label: 'Priority SLA support',
    description: 'SLA-backed support with guaranteed response times',
    icon: HeadphonesIcon,
    growth: false,
    enterprise: true,
  },
  {
    key: 'dedicated_am',
    label: 'Dedicated account manager',
    description: 'Named account manager for onboarding and support',
    icon: UserRoundCogIcon,
    growth: false,
    enterprise: true,
  },
]

const TIER_PRICING: Record<string, { label: string; color: string }> = {
  growth: { label: 'Growth', color: 'text-blue-600 dark:text-blue-400' },
  enterprise: { label: 'Enterprise', color: 'text-purple-600 dark:text-purple-400' },
}

function PendingPlanPayment({
  pendingChange,
  bankDetails,
  paying,
  onPayWithCard,
}: {
  pendingChange: NonNullable<EmployerSubscription['pending_change']>
  bankDetails: EmployerBankDetails | null
  paying: boolean
  onPayWithCard: () => void
}) {
  return (
    <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-5">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
          <ClockIcon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Plan change awaiting payment</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {pendingChange.plan_name} · {pendingChange.billing_cycle === 'annual' ? 'annual' : 'monthly'} · {formatCentsWithCurrency(pendingChange.amount_due_cents, pendingChange.currency)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your current plan stays active until PeerPass confirms this payment.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={onPayWithCard} disabled={paying} className="gap-1.5">
              {paying ? <LoaderIcon className="size-4 animate-spin" /> : <CreditCardIcon className="size-4" />}
              {paying ? 'Opening Stripe…' : 'Pay by card'}
            </Button>
            <Link
              to="/dashboard/billing/invoices/$invoiceId"
              params={{ invoiceId: String(pendingChange.invoice_id) }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-muted"
            >
              <FileTextIcon className="size-3.5" />View payment options
            </Link>
          </div>
          {bankDetails && (
            <div className="mt-4 rounded-lg border border-border/70 bg-background/70 p-3">
              <p className="text-xs font-semibold text-foreground">Prefer direct transfer?</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Transfer {formatCentsWithCurrency(pendingChange.amount_due_cents, pendingChange.currency)} to {bankDetails.account_holder} at {bankDetails.bank_name}, then quote invoice #{pendingChange.invoice_id} as the payment reference. A PeerPass administrator will confirm the transfer before the plan changes.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TierFeatureComparison({ tier, planPricePerUserCents, currency, onRequestUpgrade }: {
  tier: 'growth' | 'enterprise' | null
  planPricePerUserCents: number
  currency: string
  onRequestUpgrade?: (tier: 'growth' | 'enterprise') => void
}) {
  const currentTier = tier || 'growth'
  const tierNames: Array<'growth' | 'enterprise'> = ['growth', 'enterprise']
  const tierIndex = tierNames.indexOf(currentTier)

  // Find the next tier to upgrade to (only Enterprise is above Growth)
  const nextTier = tierIndex < 1 ? tierNames[tierIndex + 1] : null
  const nextTierConfig = nextTier ? TIER_PRICING[nextTier] : null
  const nextTierPrice = nextTier && planPricePerUserCents > 0 ? formatCentsWithCurrency(planPricePerUserCents, currency) : formatCentsWithCurrency(1500, currency)

  // Feature counts
  const totalFeatures = TIER_FEATURES.length
  const unlockedFeatures = TIER_FEATURES.filter(f => f[currentTier]).length
  const lockedFeatures = totalFeatures - unlockedFeatures

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <RocketIcon className="size-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Plan features</h3>
          <p className="text-xs text-muted-foreground">
            {unlockedFeatures} of {totalFeatures} features unlocked
            {lockedFeatures > 0 && ` · ${lockedFeatures} locked`}
          </p>
        </div>
      </div>

      {/* Feature progress bar */}
      <div className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${(unlockedFeatures / totalFeatures) * 100}%`,
            backgroundColor: BRAND_MINT,
          }}
        />
      </div>

      {/* Feature grid */}
      <div className="grid gap-3 sm:grid-cols-2">
        {TIER_FEATURES.map((feature) => {
          const isIncluded = feature[currentTier]
          const Icon = feature.icon

          return (
            <div
              key={feature.key}
              className={`flex items-start gap-3 rounded-lg p-3 transition-colors ${
                isIncluded
                  ? 'bg-emerald-500/[0.04]'
                  : 'bg-muted/30 opacity-60'
              }`}
            >
              <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
                isIncluded ? 'bg-emerald-500/10' : 'bg-muted'
              }`}>
                {isIncluded ? (
                  <CheckCircleIcon className="size-4 text-emerald-500" />
                ) : (
                  <LockKeyholeIcon className="size-3.5 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={`size-3.5 shrink-0 ${isIncluded ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                  <p className={`text-xs font-medium ${isIncluded ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {feature.label}
                  </p>
                </div>
                <p className="mt-0.5 text-[10px] text-muted-foreground/60">{feature.description}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Upgrade CTA */}
      {nextTier && nextTierConfig && lockedFeatures > 0 && (
        <div className="mt-5 rounded-xl border border-dashed border-blue-500/30 bg-blue-500/[0.04] p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                <ArrowUpCircleIcon className="size-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Unlock {lockedFeatures} more feature{lockedFeatures > 1 ? 's' : ''} with {nextTierConfig.label}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {nextTierPrice}/user · Upgrade anytime to access{lockedFeatures > 1 ? ' these features' : ' this feature'}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {TIER_FEATURES.filter(f => !f[currentTier] && f[nextTier!]).map(f => (
                    <span key={f.key} className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                      <f.icon className="size-2.5" />
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg bg-blue-500 px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/25"
              onClick={() => onRequestUpgrade?.(nextTier)}
            >
              Upgrade to {nextTierConfig.label}
            </button>
          </div>
        </div>
      )}

      {/* Enterprise = all unlocked */}
      {currentTier === 'enterprise' && (
        <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-4">
          <div className="flex items-center gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
              <CheckCircleIcon className="size-4 text-emerald-500" />
            </div>
            <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              All features unlocked — you're on the highest tier
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function TierBadge({ tier }: { tier: 'growth' | 'enterprise' }) {
  const config = {
    growth:   { icon: TrendingUpIcon, label: 'Growth', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    enterprise: { icon: ShieldCheckIcon, label: 'Enterprise', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  }
  const c = config[tier] || config.growth
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.color}`}>
      <c.icon className="size-3" />
      {c.label}
    </span>
  )
}

function UpgradeConfirmDialog({
  open,
  currentTier,
  targetTier,
  isBillingCycleChange,
  pricePerUserCents,
  targetPricePerUserCents,
  activeUsers,
  currency,
  upgrading,
  onConfirm,
  onCancel,
}: {
  open: boolean
  currentTier: 'growth' | 'enterprise' | null
  targetTier: 'growth' | 'enterprise' | null
  isBillingCycleChange: boolean
  pricePerUserCents: number
  targetPricePerUserCents: number
  activeUsers: number
  currency: string
  upgrading: boolean
  onConfirm: () => Promise<void>
  onCancel: () => void
}) {
  const currentTierConfig = currentTier ? TIER_PRICING[currentTier] : null
  const targetTierConfig = targetTier ? TIER_PRICING[targetTier] : null

  // For billing cycle changes (same tier, switching to annual):
  // show the annual savings instead of tier comparison
  const annualDiscount = currentTier === 'growth' ? 0.29 : 0.20
  const annualPricePerUser = Math.round(pricePerUserCents * (1 - annualDiscount))
  const annualEstimate = annualPricePerUser * activeUsers
  const currentEstimate = pricePerUserCents * activeUsers
  const targetPerUser = isBillingCycleChange ? annualPricePerUser : targetPricePerUserCents
  const targetEstimate = isBillingCycleChange ? annualEstimate : targetPricePerUserCents * activeUsers
  const diff = isBillingCycleChange ? -(currentEstimate - annualEstimate) : targetEstimate - currentEstimate

  const dialogTitle = isBillingCycleChange
    ? `Switch to annual billing`
    : `Upgrade to ${targetTierConfig?.label || ''}`
  const dialogDesc = isBillingCycleChange
    ? `Save ${Math.round(annualDiscount * 100)}% by paying annually.`
    : 'Review the pricing change before confirming.'
  const confirmLabel = isBillingCycleChange
    ? `Switch to annual`
    : `Confirm upgrade to ${targetTierConfig?.label || ''}`

  if (!currentTierConfig || !targetTierConfig) return null

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={dialogTitle}
      description={dialogDesc}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={upgrading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={upgrading}
            className="gap-1.5 bg-blue-500 hover:bg-blue-600"
          >
            {upgrading ? (
              <LoaderIcon className="size-4 animate-spin" />
            ) : (
              <ArrowUpCircleIcon className="size-4" />
            )}
            {upgrading ? 'Upgrading…' : confirmLabel}
          </Button>
        </>
      }
    >
      {/* Price comparison */}
      <div className="space-y-4">
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Pricing comparison</p>
          <div className="space-y-3">
            {/* Current */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex size-6 items-center justify-center rounded bg-muted text-xs font-medium text-muted-foreground">
                  {currentTierConfig.label[0]}
                </span>
                <span className="text-sm font-medium text-foreground">{currentTierConfig.label}</span>
              </div>
              <span className="text-sm tabular-nums text-muted-foreground">
                {formatCentsWithCurrency(pricePerUserCents, currency)}/user × {activeUsers} user{activeUsers !== 1 ? 's' : ''}
                {' = '}
                <span className="font-medium text-foreground">{formatCentsWithCurrency(currentEstimate, currency)}/mo</span>
              </span>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center">
              <ArrowUpCircleIcon className="size-4 text-blue-500" />
            </div>

            {/* Target */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex size-6 items-center justify-center rounded bg-blue-500/10 text-xs font-medium text-blue-600 dark:text-blue-400">
                  {targetTierConfig.label[0]}
                </span>
                <span className="text-sm font-medium text-foreground">{targetTierConfig.label}</span>
              </div>
              <span className="text-sm tabular-nums text-muted-foreground">
                {formatCentsWithCurrency(targetPerUser, currency)}/user × {activeUsers} user{activeUsers !== 1 ? 's' : ''}
                {' = '}
                <span className="font-medium text-foreground">{formatCentsWithCurrency(targetEstimate, currency)}/mo</span>
              </span>
            </div>
          </div>

          {/* Diff */}
          {diff !== 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Estimated change</span>
                <span className={`text-sm font-semibold tabular-nums ${diff > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {diff > 0 ? '+' : ''}{formatCentsWithCurrency(diff, currency)}/mo
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          <li className="flex items-start gap-2">
            <CheckCircleIcon className="mt-0.5 size-3 shrink-0 text-emerald-500" />
            <span>Your current plan stays active until payment is confirmed. The selected plan is applied after Stripe or PeerPass confirms payment.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircleIcon className="mt-0.5 size-3 shrink-0 text-emerald-500" />
            <span>You can review the invoice and choose secure card payment or direct bank transfer.</span>
          </li>
        </ul>
      </div>
    </Dialog>
  )
}

function BillingField({ icon, label, value, badge }: { icon: React.ReactNode; label: string; value: string; badge?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <p className="truncate text-sm font-medium text-foreground flex items-center gap-2">
          {value}
          {badge && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                    {badge}
                    <InfoIcon className="size-2.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-56">
                  A platform administrator has set a custom pricing rate for your organization.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
      </div>
    </div>
  )
}
